"use client";

import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const specs = [{
  category: "INDÚSTRIA",
  title: "Sistemas de lubrificação.",
  description: "Soluções precisas para redução de atrito e desgaste em linhas de produção contínua."
}, {
  category: "QUÍMICA",
  title: "Controlo de reações.",
  description: "Injeção controlada de reagentes para otimização de processos químicos industriais."
}, {
  category: "ALIMENTAR",
  title: "Higienização e limpeza.",
  description: "Sistemas de pulverização para desinfeção e lavagem de superfícies e equipamentos."
}, {
  category: "AMBIENTE",
  title: "Controlo de emissões.",
  description: "Tecnologia para supressão de poeiras e tratamento de gases em chaminés industriais."
}, {
  category: "METALURGIA",
  title: "Arrefecimento técnico.",
  description: "Pulverização de alta performance para arrefecimento controlado de metais em fusão."
}, {
  category: "AGRICULTURA",
  title: "Tratamento de culturas.",
  description: "Bicos de pulverização de alta precisão para aplicação eficiente de fitofármacos."
}, {
  category: "ENGENHARIA",
  title: "Projetos customizados.",
  description: "Desenvolvimento de sistemas de engenharia adaptados às necessidades específicas do cliente."
}];
export default function Applications() {
  const container = useRef<HTMLDivElement>(null);
  const leftColRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add({
      isDesktop: "(min-width: 1024px) and (prefers-reduced-motion: no-preference)",
      isMobile: "(max-width: 1023px) and (prefers-reduced-motion: no-preference)",
      isReduced: "(prefers-reduced-motion: reduce)"
    }, ctx => {
      const {
        isDesktop,
        isMobile,
        isReduced
      } = ctx.conditions as {
        isDesktop: boolean;
        isMobile: boolean;
        isReduced: boolean;
      };
      if (isReduced) {
        gsap.set(".spec-card, .left-eyebrow, .left-heading, .left-body, .left-stat, .left-cta", {
          opacity: 1,
          y: 0,
          filter: "blur(0px)"
        });
        return;
      }
      gsap.set([".left-eyebrow", ".left-heading", ".left-body", ".left-stat", ".left-cta"], {
        y: 24
      });
      gsap.to([".left-eyebrow", ".left-heading", ".left-body", ".left-stat", ".left-cta"], {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      if (isDesktop && leftColRef.current) {
        ScrollTrigger.create({
          trigger: el,
          start: "top top+=100",
          end: "bottom bottom",
          pin: leftColRef.current,
          pinSpacing: false
        });
      }
      gsap.set(".spec-card", {
        y: 24
      });
      gsap.utils.toArray<HTMLElement>(".spec-card").forEach(card => {
        gsap.to(card, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 88%",
            once: true
          }
        });
      });
      void isMobile;
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="31225" id="highprecisionapplications" className="bg-background text-foreground py-32 md:py-44 relative overflow-hidden">
      <div ref={container} className="mx-auto max-w-[1360px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div ref={leftColRef} className="lg:col-span-5 relative">
            {}
            <div aria-hidden="true" className="pointer-events-none absolute -top-20 -left-20 w-[400px] h-[400px] rounded-lg opacity-40" style={{
            background: "radial-gradient(circle at center, hsl(215 90% 45% / 0.18) 0%, hsl(215 90% 45% / 0.06) 40%, transparent 70%)",
            filter: "blur(40px)"
          }} />

            <div className="relative">
              <div className="left-eyebrow inline-flex items-center gap-2 mb-8" style={{
              opacity: 0
            }}>
                <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-lg" style={{
                backgroundColor: "hsl(215 90% 45%)"
              }} />
                <span className="text-xs font-medium uppercase text-muted-foreground" style={{
                letterSpacing: "0.22em"
              }}>
                  APLICAÇÕES
                </span>
              </div>

              <h2 className="left-heading font-default text-foreground" style={{
              opacity: 0,
              fontSize: "clamp(2rem, 4.6vw, 4rem)",
              fontWeight: 480,
              letterSpacing: "-0.03em",
              lineHeight: 1.05
            }}>
                Engenharia de precisão industrial.
              </h2>

              <p className="left-body mt-8 text-muted-foreground max-w-[400px]" style={{
              opacity: 0,
              fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
              fontWeight: 350,
              lineHeight: 1.55,
              letterSpacing: "-0.005em"
            }}>
                Soluções avançadas em sistemas de pulverização, otimizando processos produtivos com tecnologia de ponta desde 1937.
              </p>

              {}
              <div className="left-stat mt-10 rounded-3xl p-6 max-w-[340px]" style={{
              opacity: 0,
              backgroundColor: "hsl(210 10% 92%)",
              color: "hsl(215 80% 20%)"
            }}>
                <div className="font-default" style={{
                fontSize: "clamp(3rem, 6vw, 4.5rem)",
                fontWeight: 380,
                letterSpacing: "-0.045em",
                lineHeight: 0.95
              }}>
                  1993
                </div>
                <div className="mt-3 text-sm" style={{
                color: "hsl(215 12% 40%)",
                fontWeight: 400,
                letterSpacing: "0.01em"
              }}>
                  Representação Exclusiva
                </div>
              </div>

              <div className="left-cta mt-8" style={{
              opacity: 0
            }}>
                <Button variant="ghost" className="group h-auto px-0 py-2 text-foreground hover:bg-transparent hover:text-primary">
                  <span className="text-sm font-medium">Ver catálogo técnico</span>
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5 group-hover:-translate-y-0.5" />
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 flex flex-col gap-4 lg:gap-5">
            {specs.map((spec, i) => <div key={i} data-index={i} className="spec-card group rounded-3xl p-6 lg:p-8 transition-all duration-300 hover:-translate-y-1" style={{
            opacity: 0,
            background: "hsla(210, 10%, 92%, 0.3)",
            backdropFilter: "blur(28px) saturate(140%)",
            WebkitBackdropFilter: "blur(28px) saturate(140%)",
            border: "1px solid hsla(210, 10%, 85%, 0.5)"
          }} onMouseEnter={e => {
            e.currentTarget.style.border = "1px solid hsl(215 90% 45%)";
            e.currentTarget.style.boxShadow = "0 24px 80px hsla(215 90% 45%, 0.1), inset 0 0 60px hsla(215 90% 45%, 0.05)";
          }} onMouseLeave={e => {
            e.currentTarget.style.border = "1px solid hsla(210, 10%, 85%, 0.5)";
            e.currentTarget.style.boxShadow = "none";
          }}>
                <div className="grid grid-cols-1 sm:grid-cols-[30%_1fr] gap-4 sm:gap-6 items-start">
                  <div>
                    <span className="inline-block text-xs font-medium uppercase transition-colors duration-300 group-hover:text-primary" style={{
                  color: "hsl(215 90% 45%)",
                  letterSpacing: "0.22em"
                }}>
                      {spec.category}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-default text-foreground" style={{
                  fontSize: "clamp(1rem, 1.2vw, 1.125rem)",
                  fontWeight: 500,
                  letterSpacing: "-0.005em",
                  lineHeight: 1.35
                }}>
                      {spec.title}
                    </h3>
                    <p className="mt-3 text-muted-foreground" style={{
                  fontSize: "clamp(0.9375rem, 1vw, 1rem)",
                  fontWeight: 350,
                  lineHeight: 1.6
                }}>
                      {spec.description}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}